import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { categoryName, coverOf, searchWorks } from "@/lib/content";

interface SearchFieldProps {
  autoFocus?: boolean;
  size?: "md" | "lg";
  placeholder?: string;
  onDone?: () => void;
}

export default function SearchField({
  autoFocus = false,
  size = "md",
  placeholder = "搜索作品名称、类型或导演",
  onDone,
}: SearchFieldProps) {
  const [keyword, setKeyword] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (autoFocus) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 120);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [autoFocus]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const suggestions = useMemo(() => searchWorks(keyword, 6), [keyword]);
  const showPanel = focused && keyword.trim().length > 0;

  const submit = (value?: string) => {
    const text = (value ?? keyword).trim();
    if (!text) return;
    setFocused(false);
    onDone?.();
    navigate(`/category/all?keyword=${encodeURIComponent(text)}`);
  };

  const sizeClass = size === "lg" ? "h-14 pl-12 pr-28 text-[15px]" : "h-11 pl-10 pr-4 text-sm";

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative w-full">
        <i
          className={`ri-search-line pointer-events-none absolute top-1/2 -translate-y-1/2 text-foreground-500 ${
            size === "lg" ? "left-4 text-lg" : "left-3 text-base"
          }`}
        />
        <input
          ref={inputRef}
          type="search"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
          placeholder={placeholder}
          aria-label="搜索作品"
          className={`w-full rounded-lg border border-background-300/70 bg-background-100/90 text-foreground-100 placeholder:text-foreground-600 outline-none transition focus:border-primary-500/80 focus:bg-background-100 ${sizeClass}`}
        />
        {size === "lg" ? (
          <button
            type="button"
            onClick={() => submit()}
            className="absolute right-2 top-1/2 flex h-10 -translate-y-1/2 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-950 transition hover:bg-primary-400"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        ) : null}
      </div>

      {showPanel ? (
        <div className="fade-in absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-background-300/70 bg-background-100">
          {suggestions.length > 0 ? (
            <ul className="max-h-[340px] overflow-y-auto py-1.5">
              {suggestions.map((work) => (
                <li key={work.id}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => submit(work.title)}
                    className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left transition hover:bg-background-200"
                  >
                    <img
                      src={coverOf(work)}
                      alt={work.title}
                      className="h-12 w-9 flex-shrink-0 rounded object-cover object-top"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-100">{work.title}</span>
                      <span className="mt-0.5 block truncate text-[11px] text-foreground-500">
                        {categoryName(work.category)} · {work.region} · {work.year}
                      </span>
                    </span>
                    <i className="ri-arrow-right-up-line text-foreground-600" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-foreground-500">
              未找到「{keyword}」相关的作品
            </div>
          )}
          <button
            type="button"
            onClick={() => submit()}
            className="flex w-full cursor-pointer items-center justify-between border-t border-background-300/60 px-3 py-2.5 text-[12px] text-primary-300 transition hover:bg-background-200"
          >
            <span>全站搜索「{keyword.trim()}」</span>
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      ) : null}
    </div>
  );
}